import React, { useState, useEffect, useRef } from 'react';
import { Collapse, Box, UnstyledButton, Progress } from '@mantine/core';
import { ThemedText } from './Typography';
import {
  IconArticle,
  IconBell,
  IconBook2,
  IconBookmarks,
  IconBriefcase,
  IconBriefcase2,
  IconCalendarClock,
  IconCalendarEvent,
  IconCalendarPlus,
  IconCalendarWeek,
  IconChevronDown,
  IconClipboardText,
  IconCoin,
  IconComponents,
  IconEdit,
  IconFileImport,
  IconHelp,
  IconInfoCircle,
  IconLogin,
  IconLogout,
  IconMail,
  IconMapPin,
  IconMusic,
  IconNetwork,
  IconPalette,
  IconPlus,
  IconSchool,
  IconSettings,
  IconShield,
  IconLayoutDashboard,
  IconShoppingCartSearch,
  IconSparkles,
  IconCrown,
  IconStars,
  IconUserPlus,
  IconVideo,
  IconX,
  type Icon,
} from '@tabler/icons-react';
import { Link, usePage, router } from '@inertiajs/react';
import '../styles/Sidebar.css';
import { useUser } from '../src/context/UserContext';
import { ThemedLoader, CountBadge } from './ui';
import { COLORS } from '../src/constants/theme';
import { fetchHubCounts } from '../src/context/AdminHubContext';

interface SidebarProps {
  user?: {
    id: number;
    email: string;
    username: string;
    roles: string[];
    avatar_url: string | null;
    organizer_ids?: number[];
  } | null;
  isOpen: boolean;
  closeSidebar: () => void;
}

type NavItem = {
  label: string;
  icon: Icon;
  link: string;
  roles?: string[];
  requiresLogin?: boolean;
  requiresOrganizer?: boolean;
  /** Shows the Admin Hub needs-attention total as an orange badge */
  showsAdminAttention?: boolean;
};

type NavGroup = {
  title: string;
  icon: Icon;
  items?: NavItem[];
  subgroups?: { title: string; items: NavItem[] }[];
};

const SUPER_ADMIN = ['SuperAdmin'];
const ADMINS = ['SuperAdmin', 'Admin'];
const EDITORS = ['SuperAdmin', 'Editor'];
// Who can open the Admin Hub (AdminHub::Access::HUB_ROLES).
const HUB_ROLES = ['SuperAdmin', 'Admin'];
const ARTIST_STAFF = ['Artist', 'Editor'];
const BLOGGERS = ['SuperAdmin', 'Blogger'];

// Everyday destinations, shown as a tile grid at the top of the drawer
const exploreLinks: NavItem[] = [
  { label: 'Events', icon: IconCalendarEvent, link: '/events' },
  { label: 'Cities', icon: IconMapPin, link: '/cities' },
  { label: 'Calendars', icon: IconCalendarWeek, link: '/dance_styles' },
  { label: 'Artists', icon: IconPalette, link: '/artists' },
  { label: 'Videos', icon: IconVideo, link: '/videos' },
  { label: 'Songs', icon: IconMusic, link: '/songs' },
  { label: 'Dictionary', icon: IconBook2, link: '/dictionary' },
  { label: 'Posts', icon: IconArticle, link: '/posts' },
];

// Personal links, shown flat for signed-in users
const yourStuffLinks: NavItem[] = [
  { label: 'Bookmarked Events', icon: IconBookmarks, link: '/bookmarked-events', requiresLogin: true },
  { label: 'Saved Videos', icon: IconBookmarks, link: '/saved-videos', requiresLogin: true },
  { label: 'City Subscriptions', icon: IconBell, link: '/subscriptions/manage', requiresLogin: true },
  {
    label: 'Your Private Lessons',
    icon: IconSchool,
    link: '/users/:username/user-settings?section=studentPrivateLessons',
    requiresLogin: true,
  },
  { label: 'Settings', icon: IconSettings, link: '/users/:username/user-settings', requiresLogin: true },
];

// Role tools, each tucked into a collapsible group
const navGroups: NavGroup[] = [
  {
    title: 'Add to the map',
    icon: IconPlus,
    items: [
      { label: 'Add an Event', icon: IconCalendarPlus, link: '/suggest_event', requiresLogin: true },
      { label: 'Add an Artist', icon: IconUserPlus, link: '/suggest_artist', requiresLogin: true },
      { label: 'Write a Post', icon: IconEdit, link: '/posts/new', roles: BLOGGERS, requiresLogin: true },
    ],
  },
  {
    title: 'Organizer',
    icon: IconBriefcase2,
    items: [
      { label: 'Events You Manage', icon: IconCalendarEvent, link: '/users/:username/managed-events', requiresLogin: true },
      {
        label: 'Your Organizations',
        icon: IconBriefcase2,
        link: '/users/:username/manage-organizations',
        roles: ['Organizer'],
        requiresLogin: true,
      },
      { label: 'Booking Hub', icon: IconBriefcase, link: '/booking-hub', requiresLogin: true, requiresOrganizer: true },
    ],
  },
  {
    title: 'Artist',
    icon: IconPalette,
    items: [
      { label: 'Your Artists', icon: IconSettings, link: '/users/:username/managed-artists', roles: ['Artist'], requiresLogin: true },
      {
        label: 'Booking Portal',
        icon: IconClipboardText,
        link: '/users/:username/your-booking-portals',
        roles: ['SuperAdmin', 'Artist'],
        requiresLogin: true,
      },
      {
        label: 'Private Lessons',
        icon: IconSchool,
        link: '/users/:username/user-settings?section=privateLessons',
        roles: ['Artist'],
        requiresLogin: true,
      },
      // /artist-pro pitches Pro, or sends Pro artists on to their Artist Pro settings.
      { label: 'Artist Pro', icon: IconCrown, link: '/artist-pro', roles: ['Artist'], requiresLogin: true },
      { label: 'Move Library', icon: IconNetwork, link: '/teaching/moves', roles: ['Artist'], requiresLogin: true },
      { label: 'Payouts', icon: IconCoin, link: '/users/:username/user-payments', roles: ARTIST_STAFF, requiresLogin: true },
      {
        label: 'Orders',
        icon: IconShoppingCartSearch,
        link: '/users/:username/artist-orders',
        roles: ARTIST_STAFF,
        requiresLogin: true,
      },
      { label: 'Supporters', icon: IconStars, link: '/users/:username/user-supporters', roles: ARTIST_STAFF, requiresLogin: true },
      { label: 'Your Posts', icon: IconEdit, link: '/posts/user_posts', roles: BLOGGERS, requiresLogin: true },
    ],
  },
  {
    title: 'Admin',
    icon: IconShield,
    subgroups: [
      {
        title: 'Dashboard',
        items: [
          { label: 'Admin Dashboard', icon: IconLayoutDashboard, link: '/admin', roles: HUB_ROLES, requiresLogin: true, showsAdminAttention: true },
        ],
      },
      {
        title: 'Review queue',
        items: [
          { label: 'Bulk Import Events', icon: IconFileImport, link: '/events/bulk_import', roles: ADMINS, requiresLogin: true },
          { label: 'Enrich Drafts (ICS)', icon: IconFileImport, link: '/events/bulk_ics_enrich', roles: ADMINS, requiresLogin: true },
        ],
      },
      {
        title: 'Directory data',
        items: [
          { label: 'Organizers', icon: IconBriefcase2, link: '/o', roles: SUPER_ADMIN, requiresLogin: true },
          { label: 'Recurring Events', icon: IconCalendarClock, link: '/re', roles: EDITORS, requiresLogin: true },
        ],
      },
      {
        title: 'System',
        items: [
          { label: 'Component Library', icon: IconComponents, link: '/admin/component-demo', roles: SUPER_ADMIN, requiresLogin: true },
        ],
      },
    ],
  },
  {
    title: 'About',
    icon: IconInfoCircle,
    items: [
      { label: 'About', icon: IconInfoCircle, link: '/about' },
      { label: "What's New", icon: IconSparkles, link: '/updates' },
      { label: 'Contact', icon: IconMail, link: '/contact' },
      { label: 'Help Center', icon: IconHelp, link: '/help' },
    ],
  },
];

const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const Sidebar: React.FC<SidebarProps> = ({ isOpen, closeSidebar, user: propUser }) => {
  const { user: contextUser, logout } = useUser();
  const user = propUser || contextUser;
  const userRoles = user?.roles || [];
  const { url: currentUrl } = usePage();

  // State to keep track of which menu item is loading
  const [loadingItem, setLoadingItem] = useState<string | null>(null);
  const [navigationProgress, setNavigationProgress] = useState(0);

  // Admin Hub needs-attention total for the Admin Dashboard link. Fetched when
  // the drawer opens, and only for roles that can open the hub.
  const [adminAttention, setAdminAttention] = useState(0);
  const isStaff = HUB_ROLES.some((role) => userRoles.includes(role));
  useEffect(() => {
    if (!isOpen || !isStaff) return;
    let cancelled = false;
    fetchHubCounts()
      .then((counts) => !cancelled && setAdminAttention(counts.attention_total))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, isStaff]);

  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);

  const handleLogout = async (e: React.FormEvent) => {
    e.preventDefault();
    await logout();
  };

  const getLoginUrl = () => {
    const currentPath = window.location.pathname;
    return `/login?return_to=${encodeURIComponent(currentPath)}`;
  };

  const getDynamicLink = (link: string) => {
    if (!user) return link;
    return link.replace(':username', user.username);
  };

  // Check if a link is currently active
  const isActiveLink = (link: string) => {
    const dynamicLink = getDynamicLink(link);
    // Remove query params for comparison
    const currentPath = currentUrl.split('?')[0];
    const linkPath = dynamicLink.split('?')[0];

    // Exact match or starts with (for nested routes)
    return currentPath === linkPath || currentPath.startsWith(linkPath + '/');
  };

  const canSee = (item: NavItem) =>
    (!item.requiresLogin || !!user) &&
    (!item.roles || item.roles.some((role) => userRoles.includes(role))) &&
    (!item.requiresOrganizer || (user?.organizer_ids?.length ?? 0) > 0);

  const visibleSubgroups = (group: NavGroup) =>
    (group.subgroups || [])
      .map((sub) => ({ ...sub, items: sub.items.filter(canSee) }))
      .filter((sub) => sub.items.length > 0);

  const visibleItems = (group: NavGroup) =>
    group.subgroups ? visibleSubgroups(group).flatMap((sub) => sub.items) : (group.items || []).filter(canSee);

  // Groups start closed, except the one holding the current page
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const isGroupOpen = (group: NavGroup) =>
    openGroups[group.title] ?? visibleItems(group).some((item) => isActiveLink(item.link));

  const toggleGroup = (group: NavGroup) => {
    setOpenGroups((prev) => ({ ...prev, [group.title]: !isGroupOpen(group) }));
  };

  // Listen for Inertia navigation events for progress indicator
  useEffect(() => {
    let progressInterval: ReturnType<typeof setInterval> | null = null;

    const startHandler = () => {
      setNavigationProgress(10);
      setLoadingItem(null);
      // Simulate progress
      progressInterval = setInterval(() => {
        setNavigationProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + 10;
        });
      }, 100);
    };

    const finishHandler = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      setNavigationProgress(100);
      setLoadingItem(null);
      // Reset progress after animation
      setTimeout(() => setNavigationProgress(0), 300);
    };

    const errorHandler = () => {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      setNavigationProgress(0);
      setLoadingItem(null);
    };

    // router.on() returns unsubscribe functions
    const unsubscribeStart = router.on('start', startHandler);
    const unsubscribeFinish = router.on('finish', finishHandler);
    const unsubscribeError = router.on('error', errorHandler);

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      unsubscribeStart();
      unsubscribeFinish();
      unsubscribeError();
    };
  }, []);

  // Close sidebar on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSidebar();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeSidebar]);

  // Prevent body scrolling when sidebar is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Lock body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleNavigate = (label: string) => {
    setLoadingItem(label);
    closeSidebar();
  };

  const renderMenuItem = (item: NavItem, keyPrefix: string) => {
    const isLoading = loadingItem === item.label;
    const isActive = isActiveLink(item.link);
    return (
      <Link
        key={`${keyPrefix}-${item.label}`}
        href={getDynamicLink(item.link)}
        className={`menu-item ${isActive ? 'active' : ''}`}
        style={isLoading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
        onClick={() => handleNavigate(item.label)}
        aria-current={isActive ? 'page' : undefined}
      >
        {isLoading ? <ThemedLoader centered={false} /> : <item.icon size={18} className="menu-icon" />}
        <ThemedText size="sm" color={isActive ? COLORS.textPrimary : COLORS.textDescription} className="menu-label">
          {item.label}
        </ThemedText>
        {item.showsAdminAttention && adminAttention > 0 && (
          <span style={{ marginLeft: 'auto' }}>
            <CountBadge count={adminAttention} tone="attention" ariaLabel={`${adminAttention} need attention`} />
          </span>
        )}
      </Link>
    );
  };

  const yourStuff = yourStuffLinks.filter(canSee);

  return (
    <>
      {/* Backdrop Overlay - Dark + Blur */}
      {isOpen && (
        <div
          ref={backdropRef}
          className="sidebar-backdrop"
          onClick={(e) => {
            // Only close if clicking directly on backdrop, not on sidebar
            if (e.target === backdropRef.current) {
              closeSidebar();
            }
          }}
          aria-hidden="true"
        />
      )}

      <div
        ref={sidebarRef}
        className={`sidebar ${isOpen ? 'open' : ''}`}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Header with Progress Indicator */}
        <Box className="nav-drawer-header">
          <div className="nav-drawer-header-row">
            <ThemedText size="lg" fw={600} className="nav-drawer-title">
              Where Can We Dance
            </ThemedText>
            <UnstyledButton onClick={closeSidebar} className="close-button" aria-label="Close menu">
              <IconX size={22} />
            </UnstyledButton>
          </div>

          {navigationProgress > 0 && (
            <Progress
              value={navigationProgress}
              size="xs"
              color={COLORS.orange}
              className="nav-drawer-progress"
              aria-label="Navigation progress"
            />
          )}
        </Box>

        <Box className="nav-drawer-body">
          {/* Explore tiles */}
          <nav aria-labelledby="sidebar-explore-label">
            <ThemedText tier="quaternary" fw={600} className="nav-drawer-label" id="sidebar-explore-label">
              Explore
            </ThemedText>
            <div className="explore-grid">
              {exploreLinks.map((item) => {
                const isLoading = loadingItem === item.label;
                const isActive = isActiveLink(item.link);
                return (
                  <Link
                    key={item.label}
                    href={item.link}
                    className={`explore-tile ${isActive ? 'active' : ''}`}
                    style={isLoading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
                    onClick={() => handleNavigate(item.label)}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {isLoading ? <ThemedLoader centered={false} /> : <item.icon size={18} className="explore-icon" />}
                    <ThemedText size="sm" fw={500} className="explore-label">
                      {item.label}
                    </ThemedText>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Personal links */}
          {yourStuff.length > 0 && (
            <nav aria-labelledby="sidebar-your-stuff-label" className="nav-drawer-flat">
              <ThemedText tier="quaternary" fw={600} className="nav-drawer-label" id="sidebar-your-stuff-label">
                Your stuff
              </ThemedText>
              {yourStuff.map((item) => renderMenuItem(item, 'yours'))}
            </nav>
          )}

          {/* Role tools */}
          <div className="nav-drawer-groups">
            {navGroups.map((group) => {
              const items = visibleItems(group);
              if (items.length === 0) return null;

              const groupId = `sidebar-group-${slugify(group.title)}`;
              const open = isGroupOpen(group);

              return (
                <div key={group.title} className={`nav-drawer-group ${open ? 'open' : ''}`}>
                  <UnstyledButton
                    onClick={() => toggleGroup(group)}
                    className="group-header"
                    aria-expanded={open}
                    aria-controls={groupId}
                  >
                    <group.icon size={20} className="group-icon" />
                    <ThemedText tier="primary" fw={600} className="group-title">
                      {group.title}
                    </ThemedText>
                    <CountBadge count={items.length} />
                    <IconChevronDown size={18} className="group-chevron" />
                  </UnstyledButton>

                  <Collapse in={open}>
                    <div className="group-items" id={groupId}>
                      {group.subgroups
                        ? visibleSubgroups(group).map((sub) => (
                            <div key={sub.title} className="group-subsection">
                              <ThemedText tier="quaternary" fw={600} className="group-sublabel">
                                {sub.title}
                              </ThemedText>
                              {sub.items.map((item) => renderMenuItem(item, sub.title))}
                            </div>
                          ))
                        : items.map((item) => renderMenuItem(item, group.title))}
                    </div>
                  </Collapse>
                </div>
              );
            })}
          </div>

          <div className="nav-drawer-footer">
            {user ? (
              <UnstyledButton onClick={handleLogout} className="menu-item nav-drawer-logout">
                <IconLogout size={18} className="menu-icon" />
                <ThemedText size="sm" color={COLORS.textDescription} className="menu-label">
                  Log out
                </ThemedText>
              </UnstyledButton>
            ) : (
              <div className="nav-drawer-auth">
                <Link href={getLoginUrl()} className="nav-drawer-auth-button nav-drawer-login">
                  <IconLogin size={18} />
                  Log in
                </Link>
                <Link href="/register" className="nav-drawer-auth-button nav-drawer-signup">
                  <IconUserPlus size={18} />
                  Sign up
                </Link>
              </div>
            )}
          </div>
        </Box>
      </div>
    </>
  );
};

export default Sidebar;
